import {
  Anchor,
  Badge,
  Blockquote,
  Box,
  Button,
  Card,
  Collapse,
  Group,
  List,
  Pill,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconClock,
  IconVideo,
} from "@tabler/icons-react";
import { generateHTML, generateJSON } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useState } from "react";
import type {
  TrainerDetailsSessionTypeOutput,
  TrainerPageOutput,
} from "@/api-types";
import { GalleryCarouselModal } from "@/components/GalleryCarouselModal";
import { Header } from "@/components/Header";
import { MultilineText } from "@/components/MultilineText";
import { Portrait } from "@/components/Portrait";
import {
  specialtyOptions,
  getSpecialtyLabel,
} from "@/pages/trainer/profile/common";
import { getLanguageInfo, getLanguageName } from "@/utils/languages";
import {
  generateSocialLink,
  getSiteColor,
  getSiteIcon,
} from "@/utils/socialLinks";
import type { MinArrayType } from "@/routers/routes";
import { alwaysArray } from "@/utils/stuff";

// Utility to check if a price is a valid, non-NaN, positive number (greater than zero)
const validPrice = (price: number | undefined | null): price is number =>
  typeof price === "number" && !Number.isNaN(price) && price > 0;

export type TrainerDetailProps = {
  trainer: TrainerPageOutput;
  sessionTypes: MinArrayType<TrainerDetailsSessionTypeOutput>;
  onBookSession: (sessionTypeId?: string) => void;
  onAskQuestion: () => void;
  isPreview?: boolean;
};

export const RenderTrainerDetail = (props: TrainerDetailProps) => {
  const { trainer, sessionTypes, onBookSession, onAskQuestion, isPreview } =
    props;

  const firstImage = trainer.gallery.find((file) => file.type === "IMAGE");
  const [expandedSessionTypes, setExpandedSessionTypes] = useState<Set<string>>(
    new Set(),
  );

  const renderDescription = (description: string) => {
    if (!description) return { __html: "" };

    // Check if it's JSON content from rich editor
    if (description.startsWith("{")) {
      try {
        const jsonContent = JSON.parse(description);
        if (jsonContent.type === "doc") {
          return {
            __html: generateHTML(jsonContent, [StarterKit]),
          };
        }
      } catch {
        // Invalid JSON, treat as plain text
      }
    }

    // For plain text, convert to HTML
    const jsonContent = generateJSON(description, [StarterKit]);
    return {
      __html: generateHTML(jsonContent, [StarterKit]),
    };
  };

  const toggleSessionType = (sessionTypeId: string) => {
    const newExpanded = new Set(expandedSessionTypes);
    if (newExpanded.has(sessionTypeId)) {
      newExpanded.delete(sessionTypeId);
    } else {
      newExpanded.add(sessionTypeId);
    }
    setExpandedSessionTypes(newExpanded);
  };

  return (
    <Stack gap="sm">
      <Header
        title={isPreview ? "Trainer Profile Preview" : "Trainer Profile"}
      />
      {firstImage ? (
        <>
          <Box
            style={{
              width: "100%",
              height: "200px",
              backgroundImage: `url(${firstImage.url})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              overflow: "hidden",
            }}
          ></Box>
          <Box mt={-75} ml="1em">
            <Portrait profile={trainer} size="8em" who="person" withBorder />
          </Box>
        </>
      ) : (
        <Box>
          <Portrait profile={trainer} size="8em" who="person" withBorder />
        </Box>
      )}

      <div>
        <Title order={2}>
          {trainer.firstName} {trainer.lastName}
        </Title>
        <Text size="lg">{trainer.headline}</Text>
        <Text size="lg" c="dimmed">
          {trainer.locality}
        </Text>
      </div>

      <Blockquote p="lg">
        <Text
          // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized
          dangerouslySetInnerHTML={renderDescription(trainer.description ?? "")}
        />
      </Blockquote>

      {trainer.links && trainer.links.length > 0 && (
        <>
          <Title order={5}>Social Links</Title>
          <Group gap="md">
            {trainer.links.map((link, index) => {
              const IconComponent = getSiteIcon(link.site);
              // If value starts with http, use as is; otherwise, reconstruct
              const linkUrl = link.value.startsWith("http")
                ? link.value
                : generateSocialLink(link);
              return (
                <Anchor
                  key={index}
                  href={linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <Group
                    gap="xs"
                    style={{
                      padding: "12px",
                      border: "1px solid #e9ecef",
                      borderRadius: "8px",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    <IconComponent
                      size={24}
                      style={{ color: getSiteColor(link.site) }}
                    />
                    <Text size="sm" c="blue">
                      {link.value.startsWith("http") || link.site === "site"
                        ? link.value
                        : linkUrl}
                    </Text>
                  </Group>
                </Anchor>
              );
            })}
          </Group>
        </>
      )}

      {trainer.gallery.length > 0 && (
        <>
          <Title order={5}>Gallery</Title>
          <GalleryCarouselModal gallery={trainer.gallery} />
        </>
      )}

      <Title order={5}>Specializations</Title>
      <Group gap="xs">
        {trainer.specialities.map((specialty) => {
          const specialtyOption = specialtyOptions.find(
            (opt) => opt.value === specialty,
          );
          return (
            <Pill key={specialty} size="md">
              {specialtyOption ? specialtyOption.label : specialty}
            </Pill>
          );
        })}
      </Group>

      <Title order={5}>Languages</Title>
      <Group gap="xs">
        {trainer.languages.map((lang) => {
          const langInfo = getLanguageInfo(lang);
          return (
            <Pill key={lang} size="md" variant="outline">
              {langInfo.emoji} {getLanguageName(lang)}
            </Pill>
          );
        })}
      </Group>

      {trainer.education && trainer.education.length > 0 && (
        <>
          <Title order={5}>Education & Certifications</Title>
          <List>
            {trainer.education.map((edu, index) => (
              <List.Item key={index}>{edu}</List.Item>
            ))}
          </List>
        </>
      )}

      {trainer.testimonials && trainer.testimonials.length > 0 && (
        <>
          <Title order={5}>Testimonials</Title>
          <Stack gap="md">
            {trainer.testimonials.map((testimonial, index) => (
              <Blockquote key={index}>
                <MultilineText text={testimonial} />
              </Blockquote>
            ))}
          </Stack>
        </>
      )}

      <Title order={5}>Individual Sessions</Title>
      <Stack gap="md">
        {sessionTypes.map((type) => {
          const isExpanded = expandedSessionTypes.has(type.id);
          return (
            <Card key={type.id} withBorder padding="md" radius="md">
              <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                  <Stack gap="xs" onClick={() => toggleSessionType(type.id)}>
                    <Group gap="xs">
                      {type.name && (
                        <Text fw={600} size="lg">
                          {type.name}
                        </Text>
                      )}
                      {isExpanded ? (
                        <IconChevronDown size={16} />
                      ) : (
                        <IconChevronRight size={16} />
                      )}
                    </Group>
                    <Group gap="xs">
                      {type.durationMinutes && (
                        <Badge
                          variant="light"
                          leftSection={<IconClock size={14} />}
                        >
                          {type.durationMinutes} min
                        </Badge>
                      )}
                      <Badge
                        variant="light"
                        color={type.online ? "blue" : "green"}
                        leftSection={
                          type.online ? <IconVideo size={14} /> : undefined
                        }
                      >
                        {type.online ? "Virtual" : "In-person"}
                      </Badge>
                      {validPrice(type.price) && (
                        <Text size="sm">€{(type.price / 100).toFixed(2)}</Text>
                      )}
                    </Group>
                  </Stack>
                  <Button
                    variant="outline"
                    onClick={() => onBookSession(type.id)}
                  >
                    Book
                  </Button>
                </Group>

                <Collapse in={isExpanded}>
                  <Stack gap="md">
                    {type.description && (
                      <div>
                        <Text size="sm" fw={500} mb="xs">
                          Description:
                        </Text>
                        <Text size="sm" c="dimmed">
                          {type.description}
                        </Text>
                      </div>
                    )}

                    {validPrice(type.price) && (
                      <div>
                        <Text size="sm" fw={500} mb="xs">
                          Price: €{(type.price / 100).toFixed(2)}
                        </Text>
                      </div>
                    )}

                    {type.included && type.included.length > 0 && (
                      <div>
                        <Text size="sm" fw={500} mb="xs">
                          What's included:
                        </Text>
                        <List size="sm">
                          {type.included.map((item, index) => (
                            <List.Item key={index}>{item}</List.Item>
                          ))}
                        </List>
                      </div>
                    )}

                    {alwaysArray(type.themes).length > 0 && (
                      <div>
                        <Text size="sm" fw={500} mb="xs">
                          Themes:
                        </Text>
                        <Group gap="xs">
                          {alwaysArray(type.themes).map((theme) => (
                            <Badge
                              key={theme}
                              size="sm"
                              variant="light"
                              color="green"
                            >
                              {getSpecialtyLabel(theme)}
                            </Badge>
                          ))}
                        </Group>
                      </div>
                    )}
                  </Stack>
                </Collapse>
              </Stack>
            </Card>
          );
        })}
      </Stack>

      <SimpleGrid cols={2}>
        <Button onClick={isPreview ? () => {} : () => onBookSession()}>
          Book Session
        </Button>
        <Button bg={"second.4"} onClick={isPreview ? () => {} : onAskQuestion}>
          Ask Question
        </Button>
      </SimpleGrid>
    </Stack>
  );
};
